import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useUser, useClerk, useSession, useAuth } from '@clerk/clerk-react';
import { setUser, setLoading } from '../store/slices/authSlice';
import { analyticsService } from '../services/analyticsService';
import { auth } from '../config/firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { saveChannelsLocally } from '../utils/localStorageUtils';
import { RootState } from '../store';
import logger from '../utils/logger';

export const useClerkAuth = () => {
  const dispatch = useDispatch();
  const { user, isLoaded } = useUser();
  const { session } = useSession();
  const { signOut } = useClerk();
  const { getToken } = useAuth();
  // Get current channels from Redux for saving on logout
  const { channels } = useSelector((state: RootState) => state.playlist);

  // Sync Clerk user state with Redux and Analytics
  useEffect(() => {
    // Set loading state while Clerk is initializing
    if (!isLoaded) {
      // console.log('Clerk is still loading...');
      dispatch(setLoading(true));
      return;
    }

    // console.log('Auth state changed:', { 
    //   isLoaded, 
    //   hasUser: !!user, 
    //   hasSession: !!session
    // });

    const initializeAuth = async () => {
      try {
        // If no user or session, clear auth state
        if (!user || !session) {
          // console.log('No authenticated user, clearing auth state');
          dispatch(setUser(null));
          localStorage.removeItem('auth_user');
          analyticsService.setUserId(null);
          
          // Sign out from Firebase if needed
          if (auth.currentUser) {
            // console.log('Signing out from Firebase');
            await auth.signOut();
          }
          dispatch(setLoading(false));
          return;
        }

        // Get Firebase token from Clerk
        // console.log('Getting Firebase token');
        const firebaseToken = await getToken({ template: 'integration_firebase' });
        if (!firebaseToken) {
          throw new Error('Failed to get Firebase token from Clerk');
        }

        // Sign in to Firebase with the custom token
        // console.log('Signing in to Firebase');
        await signInWithCustomToken(auth, firebaseToken);
        // console.log('Firebase authentication successful');

        // Set up user data
        const userData = {
          uid: user.id,
          email: user.primaryEmailAddress?.emailAddress || null,
          displayName: user.fullName || null,
          createdAt: user.createdAt ? new Date(user.createdAt).toISOString() : null
        };
        
        // console.log('Setting user data');
        dispatch(setUser(userData));
        localStorage.setItem('auth_user', JSON.stringify(userData));
        
        analyticsService.setUserId(userData.uid);
        analyticsService.setUserProperties({
          email: userData.email,
          displayName: userData.displayName,
          createdAt: userData.createdAt
        });

        // console.log('Auth initialization completed');

      } catch (error) {
        // console.error('Error in auth initialization:', error);
        // Log detailed error information
        if (error instanceof Error) {
          // console.error('Error details:', {
          //   name: error.name,
          //   message: error.message,
          //   stack: error.stack
          // });
        }
        // Clear state on critical errors
        dispatch(setUser(null));
        localStorage.removeItem('auth_user');
        analyticsService.setUserId(null);
        
        if (auth.currentUser) {
          await auth.signOut();
        }
      } finally {
        dispatch(setLoading(false));
      }
    };

    initializeAuth();
  }, [user, isLoaded, dispatch, session, getToken]);

  const logout = async () => {
    try {
      // console.log('Starting logout process');
      analyticsService.trackEvent(analyticsService.events.LOGOUT);
      
      // Save current channels to local storage before logout
      if (channels.length > 0) {
        logger.info('Saving channels to local storage before logout', { count: channels.length });
        saveChannelsLocally(channels);
      }
      
      // Clear local storage auth data
      localStorage.removeItem('auth_user');
      
      // Sign out from Firebase if signed in
      if (auth.currentUser) {
        // console.log('Signing out from Firebase');
        await auth.signOut();
      }
      
      // Sign out from Clerk last
      await signOut();
      // console.log('Logout completed successfully');
    } catch (error) {
      // console.error('Error during logout:', error);
      if (error instanceof Error) {
        // console.error('Error details:', {
        //   name: error.name,
        //   message: error.message,
        //   stack: error.stack
        // });
      }
      dispatch(setUser(null));
      localStorage.removeItem('auth_user');
      throw error;
    }
  };

  return {
    logout,
    isLoading: !isLoaded,
  };
}; 